import { trimLink } from './link'
import type { ChatMention, ChatPhoto } from './types'

/** One run of a Message's text, as its bubble draws it. */
export type TextSegment =
	| { type: 'text'; text: string }
	/** A tagged member's `@Name`, exactly where the server says it sits. */
	| { type: 'mention'; text: string }
	/** An http(s) link, opened in a new tab. */
	| { type: 'link'; text: string; href: string }

const LINK = /https?:\/\/[^\s<>"]+/gi

const linkified = (text: string): TextSegment[] => {
	const segments: TextSegment[] = []
	let last = 0
	for (const match of text.matchAll(LINK)) {
		const start = match.index ?? 0
		const link = trimLink(match[0])
		if (start > last) segments.push({ type: 'text', text: text.slice(last, start) })
		segments.push({ type: 'link', text: link, href: link })
		last = start + link.length
	}
	if (last < text.length) segments.push({ type: 'text', text: text.slice(last) })
	return segments
}

/**
 * A Message's text in runs: tagged members' `@Name`s at the server's ranges (UTF-16, as
 * JS counts), links between them, and the plain text around both. A range that doesn't
 * fit the text, or overlaps one before it, is left as text.
 */
export const textSegments = (text: string, mentions: readonly ChatMention[]): TextSegment[] => {
	const segments: TextSegment[] = []
	let last = 0
	for (const { offset, length } of [...mentions].sort((a, b) => a.offset - b.offset)) {
		if (offset < last || offset + length > text.length || length <= 0) continue
		if (offset > last) segments.push(...linkified(text.slice(last, offset)))
		segments.push({ type: 'mention', text: text.slice(offset, offset + length) })
		last = offset + length
	}
	if (last < text.length) segments.push(...linkified(text.slice(last)))
	return segments
}

/** The most tiles a Message's photo grid shows; past it, the last says how many more. */
export const PHOTO_GRID_MAX_SHOWN = 4

/**
 * How a Message's photos tile, as rows of photo indexes: one shows alone at its own
 * shape; more tile two to a row, an odd first one across the top. `hidden` is how many
 * past the grid the last tile counts. Ported from iOS `ChatPhotoGridView`.
 */
export const photoGrid = (photoCount: number): { rows: number[][]; hidden: number } => {
	const shown = Math.min(photoCount, PHOTO_GRID_MAX_SHOWN)
	const rows: number[][] = []
	let index = 0
	if (shown % 2 === 1) rows.push([index++])
	while (index < shown) rows.push([index++, index++])
	return { rows, hidden: photoCount - shown }
}

/** A lone photo's height over its width, kept between a wide panorama and a tall portrait. */
export const lonePhotoRatio = (photo: Pick<ChatPhoto, 'width' | 'height'>): number => {
	if (!photo.width || !photo.height) return 0.75
	return Math.min(Math.max(photo.height / photo.width, 0.5), 1.33)
}
